import React, { useState, useMemo } from 'react';
import RichTextDisplay from './RichTextDisplay';
import { pocket } from '../api';
// Deliberately reuses UnassignedPocketNotesSection's stylesheet (same
// class names) rather than its own CSS file, so an assigned summary is
// shown with the exact same card look as an unassigned one, not just a
// similar one that drifts over time.
import './UnassignedPocketNotesSection.css';

/**
 * AssignedPocketNotesSection - every already-assigned Pocket summary
 * across all of the coach's students (materialized notes with noteType
 * 'pocket_recording'), shown as the same collapsible card list as
 * UnassignedPocketNotesSection, right below it. Each card shows which
 * student it belongs to and an Unassign action that sends it back to
 * the unassigned list - see POST /pocket/players/:playerId/notes/:noteId/unassign.
 *
 * This is coach-only bookkeeping: the student a note is assigned to
 * never sees this section or any trace of the assign/unassign
 * mechanism - to them the note simply appears or disappears from their
 * regular notes, the same as any note the coach writes and deletes.
 *
 * Expects each entry in `notes` to carry studentId/studentName alongside
 * the usual note fields (_id, text, detectedThemes, createdAt).
 */
const AssignedPocketNotesSection = ({ notes = [], allStudents = [], onUnassigned = null, onRefreshed = null, onAssignedMore = null }) => {
  const [collapsed, setCollapsed] = useState(true);
  const [expandedNoteId, setExpandedNoteId] = useState(null);
  const [unassigningId, setUnassigningId] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  // "Assign to more students" picker (one card open at a time)
  const [addingMoreId, setAddingMoreId] = useState(null);
  const [extraPlayerIds, setExtraPlayerIds] = useState([]);
  const [assigningMore, setAssigningMore] = useState(false);

  const sortedStudents = useMemo(
    () => [...allStudents].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })),
    [allStudents]
  );

  // recordingId -> Set of student ids that already hold a copy
  const holdersByRecording = useMemo(() => {
    const map = new Map();
    notes.forEach(n => {
      if (!n.sourcePocketRecordingId) return;
      const key = String(n.sourcePocketRecordingId);
      if (!map.has(key)) map.set(key, new Set());
      map.get(key).add(String(n.studentId));
    });
    return map;
  }, [notes]);

  const handleAssignMore = async (note) => {
    if (extraPlayerIds.length === 0) return;
    setAssigningMore(true);
    try {
      const res = await pocket.assignNoteToMorePlayers(note.studentId, note._id, extraPlayerIds);
      setAddingMoreId(null);
      setExtraPlayerIds([]);
      alert(res.data.assignedTo?.length
        ? `✅ Also assigned to ${res.data.assignedTo.map(a => a.name).join(', ')}`
        : 'Everyone selected already has this summary');
      if (onAssignedMore) await onAssignedMore();
    } catch (err) {
      console.error('❌ Error assigning to more students:', err);
      alert('Failed to assign: ' + (err.response?.data?.error || err.message));
    } finally {
      setAssigningMore(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleUnassign = async (note) => {
    setUnassigningId(note._id);
    setConfirmingId(null);
    try {
      await pocket.unassignNote(note.studentId, note._id);
      if (onUnassigned) onUnassigned(note._id);
    } catch (err) {
      console.error('❌ Error unassigning note:', err);
      alert('Failed to unassign note: ' + (err.response?.data?.error || err.message));
    } finally {
      setUnassigningId(null);
    }
  };

  const handleRefresh = async (note) => {
    setRefreshingId(note._id);
    setRefreshError(null);
    try {
      await pocket.refreshAssignedNote(note.studentId, note._id);
      if (onRefreshed) await onRefreshed();
    } catch (err) {
      console.error('❌ Error refreshing note:', err);
      setRefreshError(err.response?.data?.error || 'Failed to refresh from Pocket');
    } finally {
      setRefreshingId(null);
    }
  };

  // Show the section even when empty, same as UnassignedPocketNotesSection -
  // otherwise a coach with no assigned summaries yet has no way to know
  // the feature exists at all.

  return (
    <div className="unassigned-notes-section">
      <div
        className="unassigned-notes-header clickable"
        onClick={() => setCollapsed(!collapsed)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setCollapsed(!collapsed);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
      >
        <div className="unassigned-notes-header-row">
          <span className="expand-icon section-expand-icon">{collapsed ? '▶' : '▼'}</span>
          <h3>🎙️ Assigned Pocket Summaries</h3>
        </div>
        <p className="unassigned-notes-count">
          {notes.length} recording{notes.length !== 1 ? 's' : ''} assigned to students
        </p>
      </div>

      {!collapsed && (
        <div className="unassigned-notes-list">
          {notes.map(note => {
            const isExpanded = expandedNoteId === note._id;
            return (
              <div key={note._id} className={`unassigned-note-card ${isExpanded ? 'expanded' : ''}`}>
                <div
                  className="unassigned-note-header clickable"
                  onClick={() => {
                    setExpandedNoteId(isExpanded ? null : note._id);
                    setRefreshError(null);
                  }}
                >
                  <div className="unassigned-note-meta">
                    <div className="unassigned-note-title-row">
                      <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                      <h4 className="unassigned-note-player-name">
                        👤 {note.studentName || 'Unknown student'}
                      </h4>
                    </div>
                    <p className="unassigned-note-date">
                      📅 {formatDate(note.createdAt)}
                    </p>
                  </div>
                </div>

                {isExpanded && (
                  <>
                    <div className="unassigned-note-content">
                      <div className="unassigned-note-summary">
                        <RichTextDisplay content={note.text || 'No summary available'} />
                      </div>

                      <div className="unassigned-note-refresh-row">
                        <button
                          type="button"
                          onClick={() => handleRefresh(note)}
                          className="refresh-summary-button"
                          disabled={refreshingId === note._id}
                          title="Re-fetch the summary from Pocket, in case you edited it there after this was imported"
                        >
                          {refreshingId === note._id ? 'Refreshing...' : '🔄 Refresh from Pocket'}
                        </button>
                        {refreshError && (
                          <span className="refresh-error-text">{refreshError}</span>
                        )}
                      </div>

                      {(note.detectedThemes || []).length > 0 && (
                        <div className="unassigned-note-themes">
                          <strong>🏷️ Themes:</strong>
                          <div className="theme-tags">
                            {note.detectedThemes.map((theme, idx) => (
                              <span key={idx} className="theme-tag">
                                {theme}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {addingMoreId === note._id && (() => {
                      const have = holdersByRecording.get(String(note.sourcePocketRecordingId)) || new Set([String(note.studentId)]);
                      const candidates = sortedStudents.filter(s => !have.has(String(s._id)));
                      return (
                        <div className="unassigned-note-assign" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                          {candidates.length === 0 ? (
                            <span style={{ fontSize: '13px', color: '#666' }}>All your students already have this summary.</span>
                          ) : (
                            <div className="player-multiselect">
                              {candidates.map(s => (
                                <label key={s._id} className="player-multiselect-option">
                                  <input
                                    type="checkbox"
                                    checked={extraPlayerIds.includes(s._id)}
                                    disabled={assigningMore}
                                    onChange={() => setExtraPlayerIds(prev =>
                                      prev.includes(s._id) ? prev.filter(id => id !== s._id) : [...prev, s._id])}
                                  />
                                  {s.name || s.firstName}
                                </label>
                              ))}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => handleAssignMore(note)}
                              className="assign-button"
                              disabled={assigningMore || extraPlayerIds.length === 0}
                            >
                              {assigningMore ? 'Assigning...' : extraPlayerIds.length > 0 ? `Assign to ${extraPlayerIds.length}` : 'Assign'}
                            </button>
                            <button
                              onClick={() => { setAddingMoreId(null); setExtraPlayerIds([]); }}
                              className="player-select"
                              style={{ flex: 'none', cursor: 'pointer' }}
                              disabled={assigningMore}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      );
                    })()}

                    <div className="unassigned-note-assign">
                      {confirmingId === note._id ? (
                        <>
                          <span style={{ fontSize: '13px', color: '#666', flex: 1 }}>
                            Return to unassigned?
                          </span>
                          <button
                            onClick={() => handleUnassign(note)}
                            className="assign-button"
                            disabled={unassigningId === note._id}
                          >
                            {unassigningId === note._id ? 'Unassigning...' : 'Yes, unassign'}
                          </button>
                          <button
                            onClick={() => setConfirmingId(null)}
                            className="player-select"
                            style={{ flex: 'none', cursor: 'pointer' }}
                            disabled={unassigningId === note._id}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => { setAddingMoreId(note._id); setExtraPlayerIds([]); }}
                            className="assign-button"
                            disabled={addingMoreId === note._id}
                          >
                            ➕ Assign to more students
                          </button>
                          <button
                            onClick={() => setConfirmingId(note._id)}
                            className="assign-button"
                          >
                            ↩️ Unassign
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AssignedPocketNotesSection;
